import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import useNextGenSubscription from '../hooks/useNextGenSubscription';
import NextGenThemeProvider from './NextGenThemeProvider';
import NextGenHeaderLogo from './NextGenHeaderLogo';
import SessionExpiredModal from './SessionExpiredModal';
import { NexusBillingProvider, useNexusBilling } from '../context/NexusBillingContext';
import NexusQuotaExhaustedModal from './NexusQuotaExhaustedModal';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import {
  AppBar, Toolbar, Box, Typography, Avatar, Menu, MenuItem,
  ListItemIcon, ListItemText, Divider, Drawer, List, ListItem,
  ListItemButton, IconButton, Tooltip, Badge, useMediaQuery, useTheme
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useNextGenBack } from '../hooks/useNextGenBack';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';

const DRAWER_WIDTH = 248;
const DRAWER_COLLAPSED = 68;

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { label: 'Dashboard', path: '/nextgen-builder/dashboard', icon: <DashboardRoundedIcon /> },
      { label: 'Profile', path: '/nextgen-builder/profile', icon: <PersonRoundedIcon /> },
    ],
  },
  {
    label: 'Programs',
    items: [
      { label: 'Project Enrollment', path: '/nextgen-builder/enrollment', icon: <RocketLaunchRoundedIcon /> },
      // Feature-based billing (26-Aug-2026): AI Tutor, Elite Mastery and Mock
      // Interview each have a free allowance and their own paid credits, so
      // they are always visible — no subscription gate.
      { label: 'AI Tutor', path: '/nextgen-builder/ai-tutor', icon: <AutoAwesomeRoundedIcon /> },
      { label: 'Elite Mastery', path: '/nextgen-builder/elite-mastery', icon: <WorkspacePremiumRoundedIcon /> },
      { label: 'Mock Interview', path: '/nextgen-builder/mock-interview', icon: <RecordVoiceOverRoundedIcon /> },
      { label: 'Feature Credits', path: '/nextgen-builder/plans', icon: <LocalOfferRoundedIcon /> },
      { label: 'Payment History', path: '/nextgen-builder/payments', icon: <PaymentRoundedIcon /> },
    ],
  },
  {
    label: 'Earnings',
    items: [
      { label: 'Refer & Earn', path: '/nextgen-builder/refer', icon: <CardGiftcardRoundedIcon /> },
      { label: 'Wallet', path: '/nextgen-builder/wallet', icon: <AccountBalanceWalletRoundedIcon /> },
      { label: 'Support', path: '/nextgen-builder/support', icon: <HelpOutlineRoundedIcon /> },
    ],
  },
];


const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
};


const NavItem = ({ item, active, collapsed, onClick }) => (
  <ListItem disablePadding sx={{ mb: 0.25 }}>
    <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          minHeight: 42,
          px: collapsed ? 0 : 1.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: collapsed ? '10px' : '0 10px 10px 0',
          ml: collapsed ? '6px' : 0,
          mr: collapsed ? '6px' : 0,
          position: 'relative',
          background: active
            ? 'linear-gradient(90deg, rgba(108,59,255,0.1) 0%, rgba(108,59,255,0.04) 100%)'
            : 'transparent',
          borderLeft: active && !collapsed ? '3px solid #6C3BFF' : active && collapsed ? 'none' : '3px solid transparent',
          border: active && collapsed ? '1px solid rgba(108,59,255,0.25)' : undefined,
          '&:hover': {
            background: active
              ? 'linear-gradient(90deg, rgba(108,59,255,0.14) 0%, rgba(108,59,255,0.06) 100%)'
              : 'rgba(108,59,255,0.05)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 34,
            color: active ? '#6C3BFF' : '#94A3B8',
            mr: collapsed ? 0 : 0.75,
            '& svg': { fontSize: 20 },
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 700 : 500,
              color: active ? '#0F172A' : '#64748B',
              letterSpacing: '-0.005em',
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  </ListItem>
);

const NextGenLayoutInner = ({ children }) => {
  const { cartCount, authRequired, clearAuthRequired } = useNexusBilling();
  const navigate = useNavigate();
  // Same handler the page-level Back arrows use, so the two controls that
  // sit inches apart in the header can no longer disagree.
  const goBack = useNextGenBack();
  const location = useLocation();
  const { nextgen_builder, logout } = useNextGenAuth();
  const { isActive: subscriptionActive } = useNextGenSubscription();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // AI Tutor / Elite Mastery / Mock Interview render their own full-screen
  // reading/chat/interview UI — the main sidebar competes for space and adds
  // confusing double navigation chrome. Hide it entirely on those routes and
  // show a single "back to dashboard" button in its place.
  const FEATURE_ROUTE_PREFIXES = [
    '/nextgen-builder/ai-tutor',
    '/nextgen-builder/elite-mastery',
    '/nextgen-builder/mock-interview',
  ];
  const isFeatureRoute = FEATURE_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  // Hide subscription-gated Programs items until the builder has paid —
  // reuses the same subscription check as NextGenPaidFeatureGuard (the
  // route-level gate), so the sidebar and the actual route access always agree.
  const visibleNavSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.requiresSubscription || subscriptionActive),
  }));

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const profileMenuOpen = Boolean(anchorEl);
  const collapsed = !isMobile && !drawerOpen;

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/nextgen-builder/login');
  };

  // Any /api/nextgen-builder/* request that genuinely can't establish a
  // builder session (dispatched by the shared axios response interceptor
  // in utils/axiosInterceptor.js, after it's already ruled out the hard-
  // reload race by retrying once) lands here — mounted once at the layout
  // level so every NextGen Builder page gets the same proper 'please log
  // in again' popup instead of each page rendering its own raw backend
  // error text inline.
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => setSessionExpiredOpen(true);
    window.addEventListener('nextgen-builder:session-expired', handleSessionExpired);
    return () => window.removeEventListener('nextgen-builder:session-expired', handleSessionExpired);
  }, []);

  const handleSessionExpiredLoginAgain = () => {
    setSessionExpiredOpen(false);
    logout();
    navigate('/nextgen-builder/login');
  };

  const handleSessionExpiredCancel = () => {
    setSessionExpiredOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (isCollapsed) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: 1.5, pb: 1 }}>
      <Box sx={{ flex: 1, overflow: 'hidden auto' }}>
        {visibleNavSections.map((section, sectionIdx) => (
          <Box key={section.label} sx={{ mb: 0.5 }}>
            {!isCollapsed && (
              <Typography
                variant="overline"
                sx={{
                  px: 2.5, py: 0.5, display: 'block',
                  color: '#94A3B8', fontSize: '0.62rem',
                  letterSpacing: '0.1em', fontWeight: 700,
                }}
              >
                {section.label}
              </Typography>
            )}
            {isCollapsed && sectionIdx > 0 && (
              <Divider sx={{ mx: 1.5, my: 1, borderColor: '#E2E8F0' }} />
            )}
            <List disablePadding sx={{ px: isCollapsed ? 0 : 0.75 }}>
              {section.items.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  active={location.pathname === item.path}
                  collapsed={isCollapsed}
                  onClick={() => handleNavClick(item.path)}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom: Logout + Collapse */}
      <Box sx={{ px: isCollapsed ? 0 : 0.75, mt: 'auto' }}>
        <Divider sx={{ mb: 1, mx: isCollapsed ? 1.5 : 0, borderColor: '#E2E8F0' }} />
        <Tooltip title={isCollapsed ? 'Logout' : ''} placement="right" arrow>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 40,
              px: isCollapsed ? 0 : 1.5,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              borderRadius: '10px',
              mx: isCollapsed ? 0.75 : 0,
              mb: 0.5,
              color: '#EF4444',
              '&:hover': { bgcolor: 'rgba(239,68,68,0.06)', color: '#DC2626' },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: isCollapsed ? 0 : 34, color: 'inherit',
                mr: isCollapsed ? 0 : 0.75, '& svg': { fontSize: 19 },
              }}
            >
              <LogoutRoundedIcon />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, color: 'inherit' }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {!isMobile && (
          <ListItemButton
            onClick={() => setDrawerOpen(isCollapsed)}
            sx={{
              minHeight: 36,
              px: isCollapsed ? 0 : 1.5,
              justifyContent: isCollapsed ? 'center' : 'space-between',
              borderRadius: '10px',
              mx: isCollapsed ? 0.75 : 0,
              color: '#94A3B8',
              '&:hover': { bgcolor: 'rgba(108,59,255,0.05)', color: '#6C3BFF' },
            }}
          >
            {!isCollapsed && (
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                Collapse
              </Typography>
            )}
            {isCollapsed
              ? <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
              : <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />}
          </ListItemButton>
        )}
      </Box>
    </Box>
  );

  return (
    <NextGenThemeProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>

        {/* ── AppBar ── */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid #E2E8F0',
            width: '100%',
          }}
        >
          <Toolbar sx={{ minHeight: 60, px: { xs: 2, md: 2.5 }, gap: 1 }}>
            {isFeatureRoute ? (
              <Tooltip title="Back to Dashboard">
                <IconButton
                  edge="start"
                  onClick={goBack}
                  sx={{ color: '#64748B', mr: 0.5, '&:hover': { bgcolor: 'rgba(108,59,255,0.07)', color: '#6C3BFF' } }}
                >
                  <ArrowBackRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              isMobile && (
                <IconButton
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  sx={{ color: '#64748B', mr: 0.5 }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )
            )}

            {/* Logo + Brand */}
            <NextGenHeaderLogo onClick={() => navigate('/nextgen-builder/dashboard')} />


            <Box sx={{ flex: 1 }} />

            {/* Right actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title="Cart">
                <IconButton
                  onClick={() => navigate('/nextgen-builder/cart')}
                  size="small"
                  aria-label={`Cart, ${cartCount || 0} items`}
                  sx={{
                    color: '#64748B', p: 1, borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(108,59,255,0.07)', color: '#6C3BFF' },
                  }}
                >
                  <Badge badgeContent={cartCount || 0} color="primary" max={99} sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: 10, minWidth: 16, height: 16 } }}>
                    <ShoppingCartRoundedIcon sx={{ fontSize: 20 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="My Wallet">
                <IconButton
                  onClick={() => navigate('/nextgen-builder/wallet')}
                  size="small"
                  sx={{
                    color: '#64748B', p: 1, borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(108,59,255,0.07)', color: '#6C3BFF' },
                  }}
                >
                  <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>


              {/* Profile button */}
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                  pl: 1.25, pr: 1, py: 0.6, borderRadius: 2.5,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
                }}
              >
                <Avatar
                  src={nextgen_builder?.profileImage || undefined}
                  sx={{
                    width: 28, height: 28, fontSize: '0.72rem', fontWeight: 700,
                    background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)',
                    border: '1.5px solid rgba(108,59,255,0.25)',
                  }}
                >
                  {getInitials(nextgen_builder?.fullName)}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="caption"
                    sx={{ fontWeight: 700, lineHeight: 1.2, color: '#0F172A', display: 'block', fontSize: '0.8rem' }}>
                    {nextgen_builder?.fullName?.split(' ')[0] || 'Builder'}
                  </Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16, color: '#94A3B8', ml: -0.25 }} />
              </Box>
            </Box>

            {/* Profile Dropdown */}
            <Menu
              anchorEl={anchorEl}
              open={profileMenuOpen}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{ sx: { mt: 1, minWidth: 210 } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {nextgen_builder?.fullName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  @{nextgen_builder?.username}
                </Typography>
              </Box>
              <Divider />
              {[
                { label: 'My Profile', path: '/nextgen-builder/profile', icon: <PersonRoundedIcon sx={{ fontSize: 18 }} /> },
                { label: 'Dashboard', path: '/nextgen-builder/dashboard', icon: <DashboardRoundedIcon sx={{ fontSize: 18 }} /> },
                { label: 'Payments', path: '/nextgen-builder/payments', icon: <PaymentRoundedIcon sx={{ fontSize: 18 }} /> },
                { label: 'Wallet', path: '/nextgen-builder/wallet', icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} /> },
              ].map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => { setAnchorEl(null); navigate(item.path); }}
                  sx={{ py: 1.1, '&:hover': { bgcolor: 'rgba(108,59,255,0.05)' } }}
                >
                  <ListItemIcon sx={{ color: '#6C3BFF', minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500, color: '#0F172A' }} />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.1, '&:hover': { bgcolor: 'rgba(239,68,68,0.06)' } }}
              >
                <ListItemIcon sx={{ color: '#EF4444', minWidth: 32 }}>
                  <LogoutRoundedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Sign Out"
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF4444' }} />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* ── Desktop Sidebar ── */}
        {!isMobile && !isFeatureRoute && (
          <Drawer
            variant="permanent"
            sx={{
              width: collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH,
                boxSizing: 'border-box',
                top: 60,
                height: 'calc(100% - 60px)',
                bgcolor: '#FFFFFF',
                borderRight: '1px solid #E2E8F0',
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: 220,
                }),
              },
            }}
          >
            {drawerContent(collapsed)}
          </Drawer>
        )}

        {/* ── Mobile Drawer ── */}
        {isMobile && !isFeatureRoute && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                bgcolor: '#FFFFFF',
                borderRight: '1px solid #E2E8F0',
                pt: '60px',
              },
            }}
          >
            {drawerContent(false)}
          </Drawer>
        )}

        {/* ── Main Content ── */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: '60px',
            minHeight: '100vh',
            bgcolor: '#F8FAFC',
            width: 0, // prevents content from overflowing; flexGrow fills the rest
            overflowX: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>

      <SessionExpiredModal
        open={sessionExpiredOpen}
        onLoginAgain={handleSessionExpiredLoginAgain}
        onCancel={handleSessionExpiredCancel}
        isExpiringSoon={false}
      />
      {/* Feature billing: a money route (cart / checkout / orders) answered
          401 because this browser has no builder session token — sessions
          that pre-date the feature-billing launch, or an expired token.
          Same modal as the classic session-expired case. */}
      <SessionExpiredModal
        open={authRequired && !sessionExpiredOpen}
        onLoginAgain={() => { clearAuthRequired(); handleSessionExpiredLoginAgain(); }}
        onCancel={clearAuthRequired}
        isExpiringSoon={false}
      />
      <NexusQuotaExhaustedModal />
    </NextGenThemeProvider>
  );
};

/**
 * NextGenLayout — wraps every authenticated TSG Nexus page. Mounts the
 * feature-billing context once here so the header cart badge, the dashboard
 * usage cards and the reused learner modules (AI Tutor / Mock Interview /
 * Elite Mastery) all share one server-backed view of balances and cart.
 */
const NextGenLayout = ({ children }) => (
  <NexusBillingProvider>
    <NextGenLayoutInner>{children}</NextGenLayoutInner>
  </NexusBillingProvider>
);

export default NextGenLayout;
